import { useEffect, useRef, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { api } from '../../api/client';
import type { NotificationView } from '../../api/types';
import { useMe } from '../../auth/useMe';
import { useInbox } from '../../hooks/useInbox';
import { useRelativeTime } from '../../i18n';
import { severityStyle } from './severity';

/**
 * Header notification bell (ADR-0028 §4). Renders only for authenticated
 * users; the inbox poll (<c>useInbox</c>) is shared with the alert banner,
 * so mounting the bell costs no extra request.
 *
 * A bell glyph carries an unread-count badge; clicking opens a dropdown of
 * recent items (title + description + relative time + a severity dot).
 * Clicking an item marks it read and, if it carries a link, navigates
 * there (SPA route for in-app paths). "Mark all read" clears the badge in
 * one call. Outside-click and Escape close the menu, matching LocaleSwitcher.
 */
export function NotificationBell() {
    const me = useMe();
    const { inbox, refresh } = useInbox();
    const location = useLocation();
    const rel = useRelativeTime();
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);

    // Mount outside-click + Escape only while open (parity with LocaleSwitcher).
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    // Identity-gated: anonymous / loading users never see the bell.
    if (me.status !== 'authenticated') return null;

    const { items, unreadCount } = inbox;
    const badge = unreadCount > 99 ? '99+' : String(unreadCount);

    const onItemClick = (n: NotificationView) => {
        if (!n.read) {
            // Optimistic: clear locally via a refresh after the server ack.
            void api.notifications.markRead(n.id).then(refresh).catch(() => undefined);
        }
        setOpen(false);
        if (n.link) navigate(location, n.link);
    };

    const onMarkAll = () => {
        void api.notifications.markAllRead().then(refresh).catch(() => undefined);
    };

    return (
        <div ref={wrapRef} class="relative">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={
                    unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'
                }
                class="relative inline-flex h-9 w-9 items-center justify-center rounded border border-transparent text-fg-muted transition-colors hover:border-rule hover:bg-surface-raised hover:text-fg-primary"
            >
                <svg
                    width="17"
                    height="17"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                >
                    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
                </svg>
                {unreadCount > 0 && (
                    <span
                        aria-hidden="true"
                        class="absolute -right-0.5 -top-0.5 inline-flex min-w-[16px] items-center justify-center rounded-full bg-state-failed px-1 text-[10px] font-semibold leading-[16px] text-surface-base"
                    >
                        {badge}
                    </span>
                )}
            </button>

            {open && (
                <div
                    role="menu"
                    class="absolute right-0 top-full z-50 mt-1 w-[340px] overflow-hidden rounded-md border border-rule bg-surface-raised shadow-[0_12px_32px_-12px_rgba(0,0,0,0.55)]"
                >
                    <div class="flex items-center justify-between border-b border-rule px-3 py-2">
                        <span class="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-muted">
                            Notifications
                        </span>
                        {unreadCount > 0 && (
                            <button
                                type="button"
                                onClick={onMarkAll}
                                class="text-[11px] text-accent transition-colors hover:text-accent-soft"
                            >
                                Mark all read
                            </button>
                        )}
                    </div>

                    {items.length === 0 ? (
                        <p class="px-3 py-6 text-center text-[13px] text-fg-muted">
                            You're all caught up.
                        </p>
                    ) : (
                        <ul class="max-h-[60vh] divide-y divide-rule overflow-y-auto">
                            {items.map((n) => (
                                <li key={n.id}>
                                    <button
                                        type="button"
                                        onClick={() => onItemClick(n)}
                                        class={`flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-surface-base ${
                                            n.read ? '' : 'bg-accent-glow/30'
                                        }`}
                                    >
                                        <span
                                            aria-hidden="true"
                                            class={`mt-1.5 h-1.5 w-1.5 flex-none rounded-full ${severityStyle(n.severity).dot}`}
                                        />
                                        <span class="min-w-0 flex-1">
                                            <span class="flex items-baseline justify-between gap-2">
                                                <span
                                                    class={`truncate text-[13px] ${
                                                        n.read
                                                            ? 'text-fg-secondary'
                                                            : 'font-medium text-fg-primary'
                                                    }`}
                                                >
                                                    {n.title}
                                                </span>
                                                <span class="flex-none font-mono text-[10px] text-fg-faint">
                                                    {rel(n.lastSeenAt)}
                                                </span>
                                            </span>
                                            {n.description && (
                                                <span class="mt-0.5 block truncate text-[12px] text-fg-muted">
                                                    {n.description}
                                                </span>
                                            )}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * Navigate to a notification link. In-app relative paths use the SPA
 * router (no reload); absolute / external URLs fall through to a normal
 * navigation.
 */
function navigate(location: ReturnType<typeof useLocation>, link: string): void {
    if (link.startsWith('/')) {
        location.route(link);
    } else if (typeof window !== 'undefined') {
        window.location.assign(link);
    }
}
